import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ShieldCheck, Loader2 } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import Brand from "../components/Brand";

const Login = () => {
    const { login, verifyTwoFA } = useAuth();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [twofaStep, setTwofaStep] = useState(false);
    const [twofaCode, setTwofaCode] = useState("");
    const [params] = useSearchParams();
    const next = params.get("next") || "/";
    const navigate = useNavigate();

    const onSubmit = async (e) => {
        e.preventDefault();
        if (submitting) return;
        try {
            setSubmitting(true);
            const result = await login(email.trim().toLowerCase(), password);
            if (result?.twofaRequired) {
                setTwofaStep(true);
                toast("Two-factor code required", { description: "Enter the 6-digit code from your authenticator app." });
                return;
            }
            toast.success("Welcome back");
            navigate(next, { replace: true });
        } catch (err) {
            const detail = err?.response?.data?.detail;
            toast.error(typeof detail === "string" ? detail : detail?.message || "Sign in failed");
        } finally {
            setSubmitting(false);
        }
    };

    const onSubmit2FA = async (e) => {
        e.preventDefault();
        if (submitting) return;
        try {
            setSubmitting(true);
            await verifyTwoFA(email.trim().toLowerCase(), password, twofaCode.trim());
            toast.success("Welcome back");
            navigate(next, { replace: true });
        } catch (err) {
            const detail = err?.response?.data?.detail;
            toast.error(typeof detail === "string" ? detail : detail?.message || "Invalid code");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="flex min-h-screen items-center justify-center bg-white px-6 py-16" data-testid="login-page">
            <div className="absolute inset-0 -z-10 bg-brand-radial" aria-hidden />
            <div className="w-full max-w-md">
                <div className="mb-12 flex justify-center">
                    <Brand size="lg" tagline="AI-powered healthcare commerce" testId="login-brand" />
                </div>
                <div className="glass-card rounded-3xl p-8 shadow-brand">
                    {twofaStep ? (
                        <div>
                            <div className="mb-2 inline-flex items-center gap-1.5 rounded-full bg-brand-100 px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-brand-800">
                                <ShieldCheck className="h-3 w-3" /> Two-factor required
                            </div>
                            <h1 className="text-2xl font-semibold tracking-tight text-zinc-900">Enter your 6-digit code</h1>
                            <p className="mt-1 text-sm text-zinc-500">Open your authenticator app and enter the current code. You can also use a backup code.</p>
                            <form onSubmit={onSubmit2FA} className="mt-6 space-y-4" data-testid="login-twofa-form">
                                <div>
                                    <label className="label-uppercase">Authenticator code</label>
                                    <input
                                        autoFocus
                                        value={twofaCode}
                                        onChange={(e) => setTwofaCode(e.target.value)}
                                        placeholder="123456 or backup code"
                                        autoComplete="one-time-code"
                                        className="mt-2 w-full rounded-xl border border-zinc-200 bg-white px-4 py-3 text-base font-mono tracking-widest outline-none focus:border-brand-400 focus:ring-2 focus:ring-brand-200"
                                        data-testid="login-twofa-input"
                                    />
                                </div>
                                <button type="submit" disabled={submitting} className="btn-brand w-full" data-testid="login-twofa-submit">
                                    {submitting ? <Loader2 className="mx-auto h-4 w-4 animate-spin" /> : "Verify and sign in"}
                                </button>
                                <button type="button" onClick={() => { setTwofaStep(false); setTwofaCode(""); }} className="block w-full text-center text-xs text-zinc-500 hover:text-brand-700" data-testid="login-twofa-back">
                                    Use different credentials
                                </button>
                            </form>
                        </div>
                    ) : (
                        <div>
                    <h1 className="text-2xl font-semibold tracking-tight text-zinc-900">Welcome back</h1>
                    <p className="mt-1 text-sm text-zinc-500">Sign in to continue your healthcare journey.</p>

                    <form onSubmit={onSubmit} className="mt-6 space-y-4" data-testid="login-form">
                        <div>
                            <label className="label-uppercase">Email</label>
                            <input
                                type="email"
                                required
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="you@example.com"
                                className="input-clean mt-2"
                                data-testid="login-email-input"
                                autoComplete="email"
                            />
                        </div>
                        <div>
                            <label className="label-uppercase">Password</label>
                            <input
                                type="password"
                                required
                                minLength={8}
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                placeholder="••••••••"
                                className="input-clean mt-2"
                                data-testid="login-password-input"
                                autoComplete="current-password"
                            />
                        </div>
                        <button
                            type="submit"
                            disabled={submitting}
                            className="btn-brand w-full"
                            data-testid="login-submit-btn"
                        >
                            {submitting ? "Signing in..." : "Sign in"}
                        </button>
                    </form>

                    <p className="mt-6 text-center text-sm text-zinc-500">
                        New to PILLS?{" "}
                        <Link to="/signup" className="font-medium text-brand-700 hover:underline" data-testid="login-signup-link">
                            Create an account
                        </Link>
                    </p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default Login;
